import { useRef, useState, useEffect } from "react";
import { useDebounce } from "use-debounce";
import useUrlState from "@ahooksjs/use-url-state";

import { useActiveUserPermissions } from "hooks/useRoles";
import useScreenSize from "../../hooks/useScreenSize";
import CustomButton from "../CustomButton/CustomButton";
import { ConfirmDialog } from "../Dialogs";
import GoBackButton from "../GoBackButton/GoBackButton";
import PageHeader from "../PageHeader";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import TextField from "@mui/material/TextField";

import {
  FaPlus,
  FaMagnifyingGlass,
  FaTrash,
  FaEllipsisVertical,
} from "react-icons/fa6";

import "./GenericHeader.scss";

interface GenericHeaderType {
  selectedRows?: any[];
  onSearch?: any;
  model?: string;
  addResourceHandler?: () => void;
  deleteResourceHandler?: () => void;
  // For any additional buttons
  customButtonsLabels?: React.ReactNode[];
  customButtonsHandlers?: (() => void)[];
  showBackButton?: boolean;
  loading?: boolean;
}

const GenericHeader = (params: GenericHeaderType) => {
  const currentUserPermissions = useActiveUserPermissions();

  const [query, setQuery] = useUrlState({ q: "" });
  const [searchString, setSearchString] = useState<string>(query.q ?? "");
  const [value] = useDebounce(searchString, 1000);
  const device = useScreenSize();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchor = useRef<HTMLAnchorElement>(null);

  const initConfirmConfig = {
    title: "",
    body: "",
    visible: false,
  };

  useEffect(() => {
    if (params.onSearch) {
      params.onSearch(value);
    }
  }, [value]);

  const [confirmConfig, setConfirmConfig] = useState(initConfirmConfig);

  const confirmDelete = () => {
    setConfirmConfig({
      title: "Please Confirm" || "",
      body: `Are you sure you want to delete this ${params.model}?` || "",
      visible: true,
    });
  };

  return (
    <PageHeader>
      <>
        <Box className="searchBox">
          {params.showBackButton && <GoBackButton />}
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            {params.onSearch && (
              <TextField
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <FaMagnifyingGlass
                        className="searchButton"
                        color="primary"
                      />
                    </InputAdornment>
                  ),
                }}
                className="outlinedInput"
                fullWidth
                placeholder="Search..."
                variant="outlined"
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
              />
            )}
          </Box>
        </Box>
        <Box
          className="actionsBox"
          style={device === "mobile" ? { display: "flex" } : {}}
        >
          <ConfirmDialog
            setConfirmConfig={setConfirmConfig}
            confirmConfig={confirmConfig}
            executeActionCallback={params.deleteResourceHandler}
          />
          <Button
            sx={{ display: { md: "none" } }}
            ref={menuAnchor as any}
            onClick={() => setMenuOpen(true)}
          >
            <FaEllipsisVertical fontSize={18} />
          </Button>

          {/* This is a loop to generate custom buttons */}
          <Box sx={{ display: { md: "inherit", xs: "none" }, gap: "inherit" }}>
            {params.customButtonsHandlers?.map((customButtonHandler, index) => (
              <CustomButton
                disabled={params.loading}
                key={index}
                variant="contained"
                onClick={customButtonHandler}
                className="px-3 py-2"
                sx={{
                  margin: "0px 10px",
                  minHeight: "1em",
                  fontSize: "0.8em !important",
                }}
                // size="small"
              >
                {params?.customButtonsLabels &&
                params?.customButtonsLabels[index]
                  ? params?.customButtonsLabels[index]
                  : " - "}
              </CustomButton>
            ))}
            {/* Add resource button for desktop */}
            {params.addResourceHandler &&
              currentUserPermissions?.includes(`add-${params.model}`) && (
                <CustomButton
                  className="iconButton"
                  onClick={params.addResourceHandler}
                  sx={{ padding: "0px 5px" }}
                >
                  <FaPlus className="icon" />
                </CustomButton>
              )}
            {/* Delete resource button for desktop */}
            {params.deleteResourceHandler &&
              currentUserPermissions?.includes(`delete-${params.model}`) && (
                <CustomButton
                  className="iconButton"
                  onClick={confirmDelete}
                  disabled={
                    !(params.selectedRows && params.selectedRows?.length)
                  }
                  sx={{ padding: "0px 5px" }}
                >
                  <FaTrash className="icon" />
                </CustomButton>
              )}
          </Box>

          {/* This is a menu for mobile view, inside it we loop over the custom buttons */}
          <Menu
            anchorEl={menuAnchor.current}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
          >
            <Box display={"grid"} gap={"5px"}>
              {params.customButtonsHandlers?.map(
                (customButtonHandler, index) => (
                  <CustomButton
                    disabled={params.loading}
                    key={index}
                    variant="contained"
                    onClick={(e: any) => {
                      setMenuOpen(false);
                      // @ts-ignore
                      customButtonHandler(e);
                    }}
                    sx={{ padding: "5px 15px", margin: "3px", color: "white" }}
                  >
                    {params?.customButtonsLabels &&
                    params?.customButtonsLabels[index]
                      ? params?.customButtonsLabels[index]
                      : " - "}
                  </CustomButton>
                )
              )}
              {/* Add resource button for mobile */}
              {params.addResourceHandler && (
                <CustomButton
                  variant="contained"
                  onClick={params.addResourceHandler}
                  sx={{ padding: "5px 15px", margin: "3px", color: "white" }}
                >
                  <FaPlus className="icon" />
                </CustomButton>
              )}
              {/* Delete resource button for mobile */}
              {params.deleteResourceHandler && (
                <CustomButton
                  variant="contained"
                  onClick={confirmDelete}
                  disabled={params.selectedRows && !params.selectedRows?.length}
                  sx={{ padding: "5px 15px", margin: "3px", color: "white" }}
                >
                  <FaTrash className="icon" />
                </CustomButton>
              )}
            </Box>
          </Menu>
        </Box>
      </>
    </PageHeader>
  );
};

export default GenericHeader;
